import { FileText, BookOpen, BarChart3, Stethoscope, ArrowRight, Lock } from 'lucide-react';

interface ResourceCard { title: string; description: string; tag?: string; }
interface ResourceCategory { icon: React.ElementType; label: string; tagline: string; accent: string; resources: ResourceCard[]; }

const categories: ResourceCategory[] = [
  {
    icon: FileText, label: 'Clinical White Papers', tagline: 'The "Why"',
    accent: 'text-primary border-primary/20 bg-primary/5',
    resources: [
      { title: "The 'Subset Bundle' Strategy", description: "How authorizing for the \"worst-case plausible scenario\" eliminates surgical denials.", tag: 'Authorization' },
      { title: 'Defensible Documentation', description: 'A guide to meeting payer-specific CPT evidence requirements without surgeon burnout.', tag: 'Compliance' },
      { title: 'Beyond the Scribe', description: 'Why structured dictation is the only way to achieve 100% revenue capture.', tag: 'Dictation' },
    ],
  },
  {
    icon: BarChart3, label: 'Executive Fact Sheets', tagline: 'The "ROI"',
    accent: 'text-cta border-cta/20 bg-cta/5',
    resources: [
      { title: 'The 10% Leak', description: 'An analysis of uncaptured revenue in Orthopedic and Spine programs.', tag: 'Revenue' },
      { title: 'Audit Defense 101', description: "How LINK's \"Evidence Snippets\" protect hospitals from retrospective payer clawbacks.", tag: 'Audit' },
      { title: 'EPIC & Cerner Integration', description: "How LINK's \"Clean-Room Export\" accelerates HIM workflows by 80%.", tag: 'Integration' },
    ],
  },
  {
    icon: Stethoscope, label: 'Specialty Guides', tagline: 'The "How"',
    accent: 'text-chart-3 border-chart-3/20 bg-chart-3/5',
    resources: [
      { title: 'Orthopedic Procedure Integrity Guide', description: 'Complete revenue integrity playbook for TKA, Meniscus, and Arthroscopic procedures.', tag: 'Orthopedics' },
      { title: 'Spine & Neurosurgery Revenue Roadmap', description: 'Navigating complex multi-level coding, modifier sequencing, and payer-specific rules.', tag: 'Spine' },
      { title: 'Cardiology & Interventional Revenue Integrity', description: 'Authorization and documentation strategies for interventional cardiology programs.', tag: 'Cardiology' },
    ],
  },
];

export function ResourceCategories() {
  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4">
              Resource Library
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Explore by Category
            </h2>
          </div>

          <div className="space-y-16">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <div key={category.label}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${category.accent}`}>
                      <Icon className="h-4 w-4" strokeWidth={1.5} />
                      <span className="text-sm font-semibold">{category.label}</span>
                    </div>
                    <span className="text-sm text-muted-foreground font-medium italic">— {category.tagline}</span>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {category.resources.map((resource) => (
                      <button
                        key={resource.title}
                        onClick={() => {
                          const sectionMap: Record<string, string> = {
                            'Authorization': '#workflow-video', 'Compliance': '#compliance-gate', 'Dictation': '#workflow-video',
                            'Revenue': '#roi-calculator', 'Audit': '#security-trust', 'Integration': '#workflow-video',
                            'Orthopedics': '#workflow-video', 'Spine': '#workflow-video', 'Cardiology': '#workflow-video',
                          };
                          const target = resource.tag && sectionMap[resource.tag];
                          if (target) { document.querySelector(target)?.scrollIntoView({ behavior: 'smooth' }); }
                        }}
                        className="group text-left rounded-xl border border-border bg-card p-8 transition-all hover:shadow-lg"
                      >
                        {resource.tag && (
                          <span className="inline-block text-[10px] font-semibold tracking-widest uppercase text-muted-foreground mb-3">
                            {resource.tag}
                          </span>
                        )}
                        <h3 className="text-xl font-semibold text-foreground leading-snug mb-3 group-hover:text-primary transition-colors">
                          {resource.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed mb-4">{resource.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Lock className="h-3 w-3" /> Gated
                          </span>
                          <span className="flex items-center gap-1 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                            Read <ArrowRight className="h-3 w-3" />
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
